import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GradeLevelLookup } from '../../../shared-components/lookup/grade-level-lookup.model';
import { System } from '../../../shared-components/System';
import { EmailNotification } from './models/email-notification.model';
import { ParentPortalConfigurationLinks } from './models/parent-portal-configuration-links.model';
import { OtherAssociatedStudents, ParentStudentPortalDataAreas, PortalDataAreas, PortalDataAreasBlocked, PortalDataAreasSchoolStatus } from './models/portal-data-areas.model';
import { PortalFamilyDataFields } from './models/portal-family-data-fields.model';
import { PortalGeneralSettings } from './models/portal-general-settings.model';
import { StudentLoginSettings } from './models/student-login-settings.model';
import { UserDefinedFieldsForOnlineReg } from './models/user-defined-fields-for-online-reg.model';

@Injectable({
  providedIn: 'root'
})
export class ParentStudentPortalSettingsService {

  studentLoginSettings: StudentLoginSettings;
  userDefinedFieldsForOnlineReg: UserDefinedFieldsForOnlineReg[] = [];
  portalDataAreas: PortalDataAreas[] = [];
  parentStudentPortalDataAreas: ParentStudentPortalDataAreas[] = [];
  portalGeneralSettings: PortalGeneralSettings;
  portalFamilyDataFields: PortalFamilyDataFields;

  constructor(
    private http: HttpClient,
  ) { }

  initialize() {
    this.studentLoginSettings = null;
    this.userDefinedFieldsForOnlineReg = [];
    this.portalDataAreas = [];
    this.portalGeneralSettings = null;
    this.portalFamilyDataFields = null;
  }

  getStudentLoginSettings(): Observable<StudentLoginSettings> {
    return this.http.get<StudentLoginSettings>(System.apiURL.Management + 'GetStudentLoginSettings');
  }

  saveStudentLoginSettings(data: StudentLoginSettings) {
    return this.http.post(System.apiURL.Management + 'SaveStudentLoginSettings', data);
  }

  getUserDefinedFieldsForOnlineReg(): Observable<UserDefinedFieldsForOnlineReg[]> {
    return this.http.get<UserDefinedFieldsForOnlineReg[]>(System.apiURL.Management + 'GetUserDefinedFieldsForOnlineReg');
  }

  saveUserDefinedFieldsForOnlineReg(userDefinedFields: UserDefinedFieldsForOnlineReg[]) {
    return this.http.post(System.apiURL.Management + 'SaveUserDefinedFieldsForOnlineReg', userDefinedFields);
  }

  getParentPortalLinks(): Observable<ParentPortalConfigurationLinks[]> {
    return this.http.get<ParentPortalConfigurationLinks[]>(System.apiURL.Management + 'GetParentPortalLinks');
  }

  deleteParentPortalLinks(linkId: number): Observable<boolean> {
    const queryString = '?linkId=' + linkId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteParentPortalLinks' + queryString);
  }

  getPortalDataAreas(): Observable<PortalDataAreas[]> {
    return this.http.get<PortalDataAreas[]>(System.apiURL.Management + 'GetPortalDataAreas');
  }

  savePortalDataAreas(portalDataAreas: PortalDataAreas[]) {
    return this.http.post(System.apiURL.Management + 'SavePortalDataAreas', portalDataAreas);
  }

  getPortalDataAreasSchoolStatus(yearId: number): Observable<PortalDataAreasSchoolStatus[]> {
    const queryString = '?yearId=' + yearId;
    return this.http.get<PortalDataAreasSchoolStatus[]>(System.apiURL.Management + 'GetPortalDataAreasSchoolStatus' + queryString);
  }

  getParentStudentPortalDataAreas(schoolId: number): Observable<ParentStudentPortalDataAreas[]> {
    const queryString = '?schoolId=' + schoolId;
    return this.http.get<ParentStudentPortalDataAreas[]>(System.apiURL.Management + 'GetParentStudentPortalDataAreas' + queryString);
  }

  getSchoolOverrideDistrictPortalDataAreas(schoolId: number): Observable<boolean> {
    const queryString = '?schoolId=' + schoolId;
    return this.http.get<boolean>(System.apiURL.Management + 'GetSchoolOverrideDistrictPortalDataAreas' + queryString);
  }

  saveParentStudentPortalDataAreas(schoolId: number, parentStudentPortalDataAreas: ParentStudentPortalDataAreas[]) {
    const queryString = '?schoolId=' + schoolId;
    return this.http.post(System.apiURL.Management + 'SaveParentStudentPortalDataAreas' + queryString, parentStudentPortalDataAreas);
  }

  deleteParentStudentPortalDataAreas(schoolId: number) {
    const queryString = '?schoolId=' + schoolId;
    return this.http.get(System.apiURL.Management + 'DeleteParentStudentPortalDataAreas' + queryString);
  }

  getPortalEmailNotification(): Observable<EmailNotification[]> {
    return this.http.get<EmailNotification[]>(System.apiURL.Management + 'GetPortalEmailNotification');
  }

  savePortalEmailNotification(emailNotification: EmailNotification) {
    return this.http.post(System.apiURL.Management + 'SavePortalEmailNotification', emailNotification);
  }

  getPortalGeneralSettings(schoolId: number, yearId: number): Observable<PortalGeneralSettings> {
    const queryString = '?schoolId=' + schoolId +
      '&yearId=' + yearId;
    return this.http.get<PortalGeneralSettings>(System.apiURL.Management + 'GetPortalGeneralSettings' + queryString);
  }

  savePortalGeneralSettings(portalGeneralSettings: PortalGeneralSettings) {
    return this.http.post(System.apiURL.Management + 'SavePortalGeneralSettings', portalGeneralSettings);
  }

  getImageData(imageName: string): Observable<Uint8Array> {
    const queryString = '?imageName=' + imageName;
    return this.http.get<Uint8Array>(System.apiURL.Management + 'GetImageData' + queryString);
  }

  saveParentPortalLink(link: ParentPortalConfigurationLinks) {
    return this.http.post(System.apiURL.Management + 'SaveParentPortalLink', link);
  }

  getPortalFamilyDataFields(): Observable<PortalFamilyDataFields> {
    return this.http.get<PortalFamilyDataFields>(System.apiURL.Management + 'GetPortalFamilyDataFields');
  }

  savePortalFamilyDataFields(portalFamilyDataFields: PortalFamilyDataFields) {
    return this.http.post(System.apiURL.Management + 'SavePortalFamilyDataFields', portalFamilyDataFields);
  }

  getPortalDataAreasBlocked(peopledId: number): Observable<PortalDataAreasBlocked[]> {
    const queryString = '?peopleId=' + peopledId;
    return this.http.get<PortalDataAreasBlocked[]>(System.apiURL.Management + 'GetPortalDataAreasBlocked' + queryString);
  }

  savePortalDataAreasBlocked(portalDataAreasBlocked: PortalDataAreasBlocked[]) {
    return this.http.post(System.apiURL.Management + 'SavePortalDataAreasBlocked', portalDataAreasBlocked);
  }

  getAssociatedStudents(userName: string, familyId: number): Observable<OtherAssociatedStudents[]> {
    const queryString = '?userName=' + userName +
      '&familyId=' + familyId;
    return this.http.get<OtherAssociatedStudents[]>(System.apiURL.Management + 'GetOtherAssociatedStudents' + queryString);
  }
}